/**
 * The manager PIN gate.
 *
 * Settings holds the price schedule, the payout split and the PINs themselves,
 * so it sits behind a SECOND PIN that front desk staff are not given. The staff
 * PIN unlocks the app; this one unlocks the money.
 *
 * First run asks the manager to choose it, and refuses to let it match the
 * staff PIN — an identical one would hand every staff member the price list.
 *
 * The unlock lasts for the session only: navigating away and back asks again,
 * because Settings is exactly the screen someone would wander into on an
 * unattended machine.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { attemptPin, isPinSet, setPin } from '../auth/pin';
import { usePinLock } from '../auth/usePinLock';

const PIN_LENGTH = 4;

export default function ManagerGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState(false);
  const [mode, setMode] = useState<'loading' | 'enter' | 'create' | 'confirm'>('loading');
  const [entry, setEntry] = useState('');
  const [firstEntry, setFirstEntry] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { locked, lockText, note } = usePinLock('manager');

  useEffect(() => {
    isPinSet('manager').then((set) => setMode(set ? 'enter' : 'create'));
  }, []);

  useEffect(() => {
    if (mode !== 'loading') inputRef.current?.focus();
  }, [mode]);

  const submit = useCallback(async () => {
    if (entry.length !== PIN_LENGTH || busy) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'enter') {
        const attempt = await attemptPin(entry, 'manager');
        if (attempt.ok) setUnlocked(true);
        else {
          setError(note(attempt));
          setEntry('');
        }
      } else if (mode === 'create') {
        setFirstEntry(entry);
        setEntry('');
        setMode('confirm');
      } else if (mode === 'confirm') {
        if (entry === firstEntry) {
          await setPin(entry, 'manager');
          setUnlocked(true);
        } else {
          setFirstEntry('');
          setEntry('');
          setMode('create');
          setError('Those did not match — start again');
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setEntry('');
      setFirstEntry('');
      setMode('create');
    } finally {
      setBusy(false);
    }
  }, [entry, busy, mode, firstEntry, note]);

  useEffect(() => {
    if (entry.length === PIN_LENGTH) void submit();
  }, [entry, submit]);

  if (unlocked) return <>{children}</>;
  if (mode === 'loading') return <p className="py-20 text-center text-white/30">…</p>;

  const heading =
    mode === 'enter'
      ? 'Manager PIN'
      : mode === 'create'
        ? 'Choose a manager PIN'
        : 'Enter it again to confirm';

  return (
    <div className="mx-auto max-w-sm px-5 py-16 text-center">
      <div className="mb-4 text-4xl">🔐</div>
      <h1 className="mb-2 text-3xl text-brand">{heading}</h1>
      <p className="mb-8 text-sm text-white/40">
        {mode === 'enter'
          ? 'Settings holds prices and payouts. Ask the manager.'
          : 'This is separate from the front desk PIN. Do not give it to staff.'}
      </p>

      <input
        ref={inputRef}
        type="password"
        inputMode="numeric"
        maxLength={PIN_LENGTH}
        value={entry}
        disabled={busy || (mode === 'enter' && locked)}
        onChange={(e) => {
          setError('');
          setEntry(e.target.value.replace(/\D/g, ''));
        }}
        className="field tabular text-center text-3xl tracking-[0.5em]"
        placeholder="••••"
        aria-label={heading}
      />

      <p className="mt-4 h-6 text-sm font-semibold text-status-expired" role="alert">
        {mode === 'enter' && locked ? lockText : error}
      </p>
    </div>
  );
}
